import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Title,
  Text,
  Card,
  Grid,
  Stack,
  Group,
  Avatar,
  Badge,
  Divider,
  Loader,
  Center,
  Alert,
  ActionIcon,
  FileButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useSearchParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import {
  Mail,
  MapPin,
  Phone,
  Briefcase,
  Globe,
  AlertCircle,
  Edit2,
  Upload,
} from 'react-feather';
import { coachPortalApi } from '../../services/api/coachPortal';
import type { Coach } from '../../types';
import { EditCoachProfileModal } from './components/EditCoachProfileModal';
import { CalendlyConnectionCard } from './components/CalendlyConnectionCard';
import { PLATFORM_FIELD } from '../../components/coaches/meetingLinkPrompt';
import type { MissingMeetingLink } from '../../types';
import { COACH_EMAIL_MESSAGES, isMedleyEmail, medleyEmailOf } from '../../utils/medleyEmail';

// Section Header Component with optional edit action
function SectionHeader({
  title,
  onEdit,
}: {
  title: string;
  onEdit?: () => void;
}) {
  return (
    <Group justify="space-between" mb="sm">
      <Text size="xs" fw={600} c="dimmed" tt="uppercase">
        {title}
      </Text>
      {onEdit && (
        <ActionIcon variant="subtle" size="sm" onClick={onEdit} aria-label={`Edit ${title}`}>
          <Edit2 size={14} />
        </ActionIcon>
      )}
    </Group>
  );
}

// Profile Info Item Component
function ProfileInfoItem({
  icon,
  label,
  value,
  emptyText,
  warning,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
  /** MED-1355: shown in the tertiary grey when there is no value. */
  emptyText?: string;
  /** MED-1355 QA round 5: a note under the value, in the warning colour. */
  warning?: string | null;
}) {
  if (!value && !emptyText) return null;

  return (
    <Group gap="sm" wrap="nowrap">
      <Text c="dimmed">{icon}</Text>
      <div>
        <Text size="xs" c="dimmed">
          {label}
        </Text>
        {value ? (
          <Text size="sm">{value}</Text>
        ) : (
          <Text size="sm" c="var(--medley-text-tertiary)">
            {emptyText}
          </Text>
        )}
        {warning && (
          <Text size="xs" fw={500} c="var(--medley-text-warning)" mt={2}>
            {warning}
          </Text>
        )}
      </div>
    </Group>
  );
}

export function CoachProfilePage() {
  const [coach, setCoach] = useState<Coach | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  // MED-1365 QA round 6: the meeting link field that a prompt asked for.
  const [focusField, setFocusField] = useState<(typeof PLATFORM_FIELD)[MissingMeetingLink['platform']] | null>(null);
  const [connectionRefresh, setConnectionRefresh] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchProfile = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await coachPortalApi.getProfile();
      setCoach(data);
      setError(null);
    } catch {
      setError('Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleProfileSaved = (updatedCoach: Coach) => {
    setCoach(updatedCoach);
    setConnectionRefresh((n) => n + 1);
  };

  const openMeetingLink = useCallback(
    (platform: MissingMeetingLink['platform']) => {
      setFocusField(PLATFORM_FIELD[platform]);
      openEdit();
    },
    [openEdit],
  );

  const closeEditProfile = () => {
    setFocusField(null);
    closeEdit();
  };

  // MED-1365 QA round 6: the Needs Attention item links here with ?add_link=<platform>.
  useEffect(() => {
    const platform = searchParams.get('add_link');
    if (!coach || !platform) return;
    if (platform in PLATFORM_FIELD) openMeetingLink(platform as MissingMeetingLink['platform']);
    const next = new URLSearchParams(searchParams);
    next.delete('add_link');
    setSearchParams(next, { replace: true });
  }, [coach, searchParams, setSearchParams, openMeetingLink]);

  // MED-1365 QA round 9: the Needs Attention item links here with
  // ?map_calendly=1. The Calendly card opens its mapping window when its
  // status loads.
  const [mapCalendlyRequested, setMapCalendlyRequested] = useState(false);
  useEffect(() => {
    if (!searchParams.get('map_calendly')) return;
    setMapCalendlyRequested(true);
    const next = new URLSearchParams(searchParams);
    next.delete('map_calendly');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const handleMapCalendlyHandled = useCallback(() => setMapCalendlyRequested(false), []);

  const handleAvatarUpload = async (file: File | null) => {
    if (!file) return;

    try {
      setUploadingAvatar(true);
      const updatedCoach = await coachPortalApi.uploadAvatar(file);
      setCoach(updatedCoach);
      notifications.show({
        title: 'Success',
        message: 'Avatar updated successfully',
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to upload avatar',
        color: 'red',
      });
    } finally {
      setUploadingAvatar(false);
    }
  };

  if (isLoading) {
    return (
      <Container size="md" py="xl">
        <Center h={200}>
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  if (error || !coach) {
    return (
      <Container size="md" py="xl">
        <Alert icon={<AlertCircle size={16} />} color="red" title="Error">
          {error || 'Unable to load profile'}
        </Alert>
      </Container>
    );
  }

  const fullName = `${coach.first_name || ''} ${coach.last_name || ''}`.trim() || 'Unknown';
  const isActive = coach.status === 'active';

  return (
    <Container size="lg" py="xl">
      <Title order={2} mb="xl">
        My Profile
      </Title>

      <Grid gutter="lg">
        {/* Left Column - Basic Info */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="lg">
          <Card withBorder padding="lg" radius="md">
            <Group justify="flex-end">
              <ActionIcon variant="subtle" size="sm" onClick={openEdit} aria-label="Edit profile">
                <Edit2 size={14} />
              </ActionIcon>
            </Group>
            <Stack align="center" gap="md">
              <Box pos="relative" style={{ display: 'inline-block' }}>
                <Avatar
                  size={100}
                  radius="xl"
                  color="medleyGreen"
                  src={coach.avatar_url}
                >
                  {coach.first_name?.charAt(0) || 'C'}
                </Avatar>
                <FileButton onChange={handleAvatarUpload} accept="image/png,image/jpeg,image/webp">
                  {(props) => (
                    <ActionIcon
                      {...props}
                      size="sm"
                      radius="xl"
                      variant="filled"
                      color="gray"
                      pos="absolute"
                      bottom={0}
                      right={0}
                      loading={uploadingAvatar}
                      style={{ border: '2px solid white' }}
                    >
                      <Upload size={10} />
                    </ActionIcon>
                  )}
                </FileButton>
              </Box>
              <div style={{ textAlign: 'center' }}>
                <Text fw={600} size="lg">
                  {fullName}
                </Text>
                <Text c="dimmed" size="sm">
                  Coach
                </Text>
              </div>
              <Badge
                color={isActive ? 'green' : 'gray'}
                variant="light"
                size="lg"
              >
                {coach.status?.charAt(0).toUpperCase() + coach.status?.slice(1) || 'Unknown'}
              </Badge>
            </Stack>

            <Divider my="lg" />

            <Stack gap="md">
              {/* MED-1355: a coach has two addresses. Name each one, Personal
                  Email first, as on every coach screen. A non-Medley login
                  address is not a Medley email. */}
              <ProfileInfoItem
                icon={<Mail size={16} />}
                label="Personal email"
                value={coach.preferred_email}
                emptyText="Not set"
                warning={
                  isMedleyEmail(coach.preferred_email)
                    ? COACH_EMAIL_MESSAGES.storedPersonalIsMedley
                    : null
                }
              />
              <ProfileInfoItem
                icon={<Mail size={16} />}
                label="Medley email"
                value={medleyEmailOf(coach.email)}
                emptyText="Not set"
              />
              <ProfileInfoItem
                icon={<Phone size={16} />}
                label="Phone"
                value={coach.phone}
              />
              <ProfileInfoItem
                icon={<MapPin size={16} />}
                label="Location"
                value={coach.location}
              />
              <ProfileInfoItem
                icon={<Globe size={16} />}
                label="Timezone"
                value={coach.home_base_timezone}
              />
            </Stack>
          </Card>

          {/* Calendly Connection */}
          <CalendlyConnectionCard
            onAddMeetingLink={openMeetingLink}
            refreshToken={connectionRefresh}
            openMappingRequest={mapCalendlyRequested}
            onMappingRequestHandled={handleMapCalendlyHandled}
          />
          </Stack>
        </Grid.Col>

        {/* Right Column - Details */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="lg">
            {/* Professional Info */}
            <Card withBorder padding="lg" radius="md">
              <SectionHeader title="Professional Background" onEdit={openEdit} />
              <Stack gap="md">
                {coach.coaching_specializations && (
                  <ProfileInfoItem
                    icon={<Briefcase size={16} />}
                    label="Coaching Specializations"
                    value={coach.coaching_specializations}
                  />
                )}
                {coach.industry_experience && coach.industry_experience.length > 0 && (
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>
                      Industry Experience
                    </Text>
                    <Group gap="xs">
                      {coach.industry_experience.map((ind: string, idx: number) => (
                        <Badge key={idx} variant="light" color="blue">
                          {ind}
                        </Badge>
                      ))}
                    </Group>
                  </div>
                )}
                {coach.certifications && coach.certifications.length > 0 && (
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>
                      Certifications
                    </Text>
                    <Group gap="xs">
                      {coach.certifications.map((cert: string, idx: number) => (
                        <Badge key={idx} variant="light" color="violet">
                          {cert}
                        </Badge>
                      ))}
                    </Group>
                  </div>
                )}
              </Stack>
            </Card>

            {/* Bio */}
            <Card withBorder padding="lg" radius="md">
              <SectionHeader title="Bio" onEdit={openEdit} />
              {coach.bio ? (
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {coach.bio}
                </Text>
              ) : (
                <Text size="sm" c="dimmed">No bio provided</Text>
              )}
            </Card>

            {/* Learning & Development */}
            <Card withBorder padding="lg" radius="md">
              <SectionHeader title="Learning & Development" onEdit={openEdit} />
              {coach.preferred_learning_experiences && coach.preferred_learning_experiences.length > 0 ? (
                <Group gap="xs">
                  {coach.preferred_learning_experiences.map((exp: string, idx: number) => (
                    <Badge key={idx} variant="light" color="teal">
                      {exp}
                    </Badge>
                  ))}
                </Group>
              ) : (
                <Text size="sm" c="dimmed">No learning preferences set</Text>
              )}
            </Card>

            {/* Identity Information */}
            <Card withBorder padding="lg" radius="md">
              <SectionHeader title="Identity" onEdit={openEdit} />
              {(coach.race_ethnicity?.length > 0 || coach.gender_identity || coach.pronouns) ? (
                <Stack gap="xs">
                  {coach.pronouns && (
                    <Group gap="xs">
                      <Text size="sm" c="dimmed">
                        Pronouns:
                      </Text>
                      <Badge variant="light" color="blue">
                        {coach.pronouns}
                      </Badge>
                    </Group>
                  )}
                  {coach.gender_identity && (
                    <Group gap="xs">
                      <Text size="sm" c="dimmed">
                        Gender Identity:
                      </Text>
                      <Text size="sm">{coach.gender_identity}</Text>
                    </Group>
                  )}
                  {coach.race_ethnicity && coach.race_ethnicity.length > 0 && (
                    <Group gap="xs">
                      <Text size="sm" c="dimmed">
                        Race/Ethnicity:
                      </Text>
                      <Text size="sm">{coach.race_ethnicity.join(', ')}</Text>
                    </Group>
                  )}
                </Stack>
              ) : (
                <Text size="sm" c="dimmed">No identity information provided</Text>
              )}
            </Card>

            {/* Meeting Links */}
            {(coach.zoom_link || coach.teams_link || coach.google_meet_link) && (
              <Card withBorder padding="lg" radius="md">
                <SectionHeader title="Meeting Links" onEdit={openEdit} />
                <Stack gap="xs">
                  {coach.zoom_link && (
                    <Text size="sm">Zoom: {coach.zoom_link}</Text>
                  )}
                  {coach.zoom_passcode && (
                    <Text size="sm">Zoom passcode: {coach.zoom_passcode}</Text>
                  )}
                  {coach.teams_link && (
                    <Text size="sm">Teams: {coach.teams_link}</Text>
                  )}
                  {coach.google_meet_link && (
                    <Text size="sm">Google Meet: {coach.google_meet_link}</Text>
                  )}
                </Stack>
              </Card>
            )}
          </Stack>
        </Grid.Col>
      </Grid>

      {/* Edit Profile Modal */}
      <EditCoachProfileModal
        opened={editOpened}
        onClose={closeEditProfile}
        coach={coach}
        onSaved={handleProfileSaved}
        focusField={focusField}
      />
    </Container>
  );
}

export default CoachProfilePage;
